// pages/tmShared.jsx
// Primitif UI + helper yang DIPAKAI BERSAMA oleh TableMaintenance.jsx dan
// MonthlyReport.jsx (dan halaman lain yang butuh CronScheduler nanti).
//
// Diekstrak dari TableMaintenance.jsx saat tab "Monthly Report" dipisah jadi
// halaman sendiri. Tujuannya menghindari duplikasi: sebelumnya CronScheduler
// hanya bisa dipakai oleh tab di dalam file 4000 baris itu.
//
// File ini TIDAK boleh meng-import halaman apa pun (hindari circular import).

import { useState, useEffect, useCallback } from 'react';
import { api as axiosApi } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';

// ── Primitif UI ───────────────────────────────────────────────────────────────

export function Alert({ msg, type = 'info', onClose }) {
  if (!msg) return null;
  const c = {
    success: { bg: '#d1fae5', color: '#065f46', border: '#6ee7b7' },
    error:   { bg: '#fee2e2', color: '#7f1d1d', border: '#fca5a5' },
    info:    { bg: 'rgb(var(--brand-100))', color: '#1e3a5f', border: '#93c5fd' },
  }[type] || {};
  return (
    <div style={{ background: c.bg, color: c.color, border: `1px solid ${c.border}`, borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span>{msg}</span>
      {onClose && <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: c.color, fontSize: 18, lineHeight: 1, marginLeft: 8 }}>×</button>}
    </div>
  );
}

export function Badge({ text, color = '#64748b' }) {
  return (
    <span style={{ background: color + '18', color, border: `1px solid ${color}33`, borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
      {text}
    </span>
  );
}

export function Btn({ children, onClick, variant = 'default', size = 'md', disabled = false }) {
  const base = { display: 'inline-flex', alignItems: 'center', gap: 5, border: '1px solid', borderRadius: 8, cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: 'inherit', fontSize: size === 'sm' ? 12 : 13, padding: size === 'sm' ? '5px 10px' : '8px 14px', opacity: disabled ? 0.55 : 1, transition: 'all 0.15s', fontWeight: 500 };
  const variants = {
    default: { background: '#fff',      borderColor: '#e2e8f0', color: '#475569' },
    primary: { background: 'rgb(var(--brand-600))',   borderColor: 'rgb(var(--brand-600))', color: '#fff' },
    danger:  { background: '#fef2f2',   borderColor: '#fecaca', color: '#dc2626' },
    warning: { background: '#fffbeb',   borderColor: '#fde68a', color: '#d97706' },
  };
  return <button onClick={disabled ? undefined : onClick} style={{ ...base, ...variants[variant] }}>{children}</button>;
}

export const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' };

export function Empty({ icon, text }) {
  return (
    <div style={{ padding: '3rem', textAlign: 'center', color: '#cbd5e1' }}>
      <div style={{ fontSize: 40, marginBottom: 8 }}>{icon}</div>
      <div style={{ fontSize: 13 }}>{text}</div>
    </div>
  );
}

// ── useApi — pakai axios instance dari AuthContext (token otomatis via interceptor) ──

export function useApi() {
  return useCallback(async (path, opts = {}) => {
    const method = (opts.method || 'GET').toLowerCase();
    const body   = opts.body ? JSON.parse(opts.body) : undefined;
    try {
      const res = method === 'get' || method === 'delete'
        ? await axiosApi[method](path)
        : await axiosApi[method](path, body);
      return res.data;
    } catch (err) {
      throw new Error(err.response?.data?.error || err.message || 'API error');
    }
  }, []);
}

// ── Format ukuran byte ────────────────────────────────────────────────────────

export function fmtBytes(n) {
  const b = Number(n || 0);
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
}


// ── Penjadwalan cron via UI (dipakai di tab AWS Billing & AWS Cost) ─────────────
// last_run ditulis dengan datetime('now') SQLite, yang SELALU UTC — sementara
// jadwal cron berjalan pada tz job (Asia/Jakarta). Tanpa konversi, '0 6 * * 1'
// terlihat berjalan pukul 23:00 hari sebelumnya, yang membingungkan.
//
// 'Z' WAJIB ditambahkan: string DB tidak punya penanda zona, dan tanpa 'Z'
// JavaScript memperlakukannya sebagai waktu LOKAL sehingga hasilnya salah 7 jam.
export function cronLocalTime(dbTs, tz) {
  if (!dbTs) return '';
  const d = new Date(String(dbTs).replace(' ', 'T') + 'Z');
  if (isNaN(d.getTime())) return String(dbTs);
  const zone = tz || 'Asia/Jakarta';
  try {
    const p = new Intl.DateTimeFormat('en-CA', {
      timeZone: zone, hour12: false,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    }).formatToParts(d).reduce((o, x) => (o[x.type] = x.value, o), {});
    const label = zone === 'Asia/Jakarta' ? 'WIB' : zone;
    return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}:${p.second} ${label}`;
  } catch (_) {
    return String(dbTs) + ' UTC';
  }
}

// ── Penjadwalan cron via UI ───────────────────────────────────────────────────

export function CronScheduler({ api, jobKey, runConfirm, title }) {
  const { t } = useLang();
  const ti = (key, vars) => {
    let s = t(key);
    if (vars) for (const k in vars) s = s.replace(new RegExp('\\{' + k + '\\}', 'g'), vars[k]);
    return s;
  };
  const [job, setJob]       = useState(null);
  const [cronOk, setCronOk] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [freq, setFreq]     = useState('daily');
  const [hour, setHour]     = useState(6);
  const [weekday, setWeekday] = useState(1);
  const [dom, setDom]       = useState(2);
  const [custom, setCustom] = useState('0 6 * * *');
  const [tz, setTz]         = useState('Asia/Jakarta');
  const [busy, setBusy]     = useState(false);
  const [msg, setMsg]       = useState(null);

  const parseSchedule = (sched) => {
    const p = String(sched || '').trim().split(/\s+/);
    if (p.length === 5 && p[0] === '0' && p[3] === '*') {
      const h = parseInt(p[1], 10);
      if (!isNaN(h)) {
        if (p[2] === '*' && p[4] === '*') return { freq: 'daily', hour: h };
        if (p[2] === '*' && /^[0-6]$/.test(p[4])) return { freq: 'weekly', hour: h, weekday: parseInt(p[4], 10) };
        if (/^\d+$/.test(p[2]) && p[4] === '*') return { freq: 'monthly', hour: h, dom: parseInt(p[2], 10) };
      }
    }
    return { freq: 'custom' };
  };

  const load = useCallback(async () => {
    try {
      const d = await api('/api/cron');
      setCronOk(d.cronAvailable !== false);
      const j = (d.jobs || []).find(x => x.key === jobKey);
      if (j) {
        setJob(j); setEnabled(!!j.enabled); setTz(j.tz || 'Asia/Jakarta'); setCustom(j.schedule || '0 6 * * *');
        const pr = parseSchedule(j.schedule);
        setFreq(pr.freq);
        if (pr.hour != null) setHour(pr.hour);
        if (pr.weekday != null) setWeekday(pr.weekday);
        if (pr.dom != null) setDom(pr.dom);
      }
    } catch (e) { setMsg({ text: e.message, type: 'error' }); }
  }, [api, jobKey]);
  useEffect(() => { load(); }, [load]);

  const buildSchedule = () => {
    if (freq === 'daily')   return `0 ${hour} * * *`;
    if (freq === 'weekly')  return `0 ${hour} * * ${weekday}`;
    if (freq === 'monthly') return `0 ${hour} ${dom} * *`;
    return custom.trim();
  };

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      const schedule = buildSchedule();
      const r = await api(`/api/cron/${jobKey}`, { method: 'PUT', body: JSON.stringify({ enabled, schedule, tz }) });
      setMsg({ text: ti('cron.saved', { state: r.enabled ? t('cron.stateActive') : t('cron.stateInactive'), schedule: r.schedule, tz: r.tz }), type: 'success' });
      load();
    } catch (e) { setMsg({ text: e.message, type: 'error' }); }
    finally { setBusy(false); }
  };

  const runNow = async () => {
    if (!window.confirm(runConfirm || t('cron.runConfirmDefault'))) return;
    setBusy(true); setMsg(null);
    try {
      const r = await api(`/api/cron/${jobKey}/run`, { method: 'POST', body: JSON.stringify({}) });
      setMsg({ text: ti('cron.runDone', { msg: r.message || t('cron.runDoneDefault'), n: r.count ?? 0 }), type: 'success' });
      load();
    } catch (e) { setMsg({ text: ti('cron.runFail', { err: e.message }), type: 'error' }); }
    finally { setBusy(false); }
  };

  const DOW = [t('cron.dowSun'),t('cron.dowMon'),t('cron.dowTue'),t('cron.dowWed'),t('cron.dowThu'),t('cron.dowFri'),t('cron.dowSat')];
  const box = { ...inputStyle, width: 'auto', background: '#fff' };

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, marginBottom: 16, background: '#fafafa' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{title || t('cron.title')}</span>
        {job && <Badge text={enabled ? (job.running ? t('cron.active') : t('cron.activeApply')) : t('cron.inactive')} color={enabled ? '#10b981' : '#94a3b8'} />}
        {!cronOk && <Badge text={t('cron.noNodeCron')} color="#ef4444" />}
      </div>

      {msg && <Alert msg={msg.text} type={msg.type} onClose={() => setMsg(null)} />}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: '#334155' }}>
          <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} /> {t('cron.enable')}
        </label>
        <select value={freq} onChange={e => setFreq(e.target.value)} style={box}>
          <option value="daily">{t('cron.freqDaily')}</option>
          <option value="weekly">{t('cron.freqWeekly')}</option>
          <option value="monthly">{t('cron.freqMonthly')}</option>
          <option value="custom">{t('cron.freqCustom')}</option>
        </select>
        {freq === 'weekly' && (
          <select value={weekday} onChange={e => setWeekday(parseInt(e.target.value, 10))} style={box}>
            {DOW.map((d, i) => <option key={i} value={i}>{d}</option>)}
          </select>
        )}
        {freq === 'monthly' && (
          <span style={{ fontSize: 13, color: '#334155' }}>{t('cron.dayOfMonth')}&nbsp;
            <input type="number" min={1} max={28} value={dom} onChange={e => setDom(parseInt(e.target.value, 10) || 1)} style={{ ...box, width: 64 }} />
          </span>
        )}
        {freq !== 'custom' && (
          <span style={{ fontSize: 13, color: '#334155' }}>{t('cron.hour')}&nbsp;
            <select value={hour} onChange={e => setHour(parseInt(e.target.value, 10))} style={box}>
              {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
            </select>
          </span>
        )}
        {freq === 'custom' && (
          <input value={custom} onChange={e => setCustom(e.target.value)} placeholder={t('cron.customPlaceholder')} style={{ ...box, width: 160, fontFamily: 'monospace' }} />
        )}
        <span style={{ fontSize: 12, color: '#64748b' }}>{ti('cron.tzLabel', { tz })}</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <Btn size="sm" variant="primary" disabled={busy} onClick={save}>{busy ? t('cron.saving') : t('cron.saveSchedule')}</Btn>
        <Btn size="sm" disabled={busy} onClick={runNow}>{t('cron.runNow')}</Btn>
        <code style={{ fontSize: 12, color: '#475569', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, padding: '2px 8px' }}>{buildSchedule()}</code>
      </div>

      {job?.last_run && (
        <div style={{ fontSize: 12, color: '#64748b', marginTop: 10 }}>
          {t('cron.lastRun')} <strong>{cronLocalTime(job.last_run, job.tz)}</strong>{' — '}
          <span style={{ color: job.last_status === 'success' ? '#10b981' : '#ef4444', fontWeight: 600 }}>{job.last_status || '-'}</span>
          {job.last_detail ? ` (${job.last_detail})` : ''}
        </div>
      )}
    </div>
  );
}

